import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/baseQuery';
import { METHODS } from 'config';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  job: string;
}

interface SurveyQuestion {
  _id: string;
  questionText: string;
  questionType: 'text' | 'single_choice' | 'multiple_choice' | 'rating';
  options?: string[];
  isRequired: boolean;
  order: number;
}

interface Survey {
  _id: string;
  title: string;
  description?: string;
  questions: SurveyQuestion[];
  creator: User;
  status: 'active' | 'closed' | 'draft';
  isAnonymous: boolean;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface SurveyResponse {
  _id: string;
  survey: string;
  user?: User;
  answers: {
    questionId: string;
    answer: string | string[];
  }[];
  submittedAt: string;
}

interface SurveyResult {
  questionId: string;
  question: string;
  type: string;
  answers: {
    answer: string;
    count: number;
    percentage: number;
  }[];
  totalResponses: number;
}

interface SurveyListQuery {
  page?: number;
  limit?: number;
}

interface SurveyListResponse {
  data: Survey[];
  count: number;
}

interface CreateSurveyInput {
  title: string;
  description?: string;
  questions: {
    questionText: string;
    questionType: 'text' | 'single_choice' | 'multiple_choice' | 'rating';
    options?: string[];
    isRequired: boolean;
    order: number;
  }[];
  isAnonymous: boolean;
  startDate?: string;
  endDate?: string;
}

interface SubmitResponseInput {
  surveyId: string;
  answers: {
    questionId: string;
    answer: string | string[];
  }[];
}

const SurveyApi = createApi({
  reducerPath: 'survey-api',
  baseQuery: baseQuery(),
  tagTypes: ['Surveys', 'SurveyResponses'],
  endpoints: (builder) => ({
    getSurveys: builder.query<SurveyListResponse, SurveyListQuery>({
      query: (params) => ({
        url: '/survey/list',
        method: METHODS.get,
        params,
      }),
      providesTags: ['Surveys'],
    }),
    getSurvey: builder.query<{ survey: Survey; hasResponded: boolean }, string>({
      query: (surveyId) => ({
        url: `/survey/get`,
        method: METHODS.get,
        params: { surveyId },
      }),
      providesTags: ['Surveys'],
    }),
    createSurvey: builder.mutation<{ survey: Survey }, CreateSurveyInput>({
      query: (body) => ({
        url: '/survey/create',
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Surveys'],
    }),
    submitResponse: builder.mutation<{ response: SurveyResponse }, SubmitResponseInput>({
      query: ({ surveyId, ...body }) => ({
        url: `/survey/submit`,
        method: METHODS.post,
        params: { surveyId },
        body,
      }),
      invalidatesTags: ['Surveys', 'SurveyResponses'],
    }),
    getSurveyResults: builder.query<{ results: SurveyResult[]; totalResponses: number }, string>({
      query: (surveyId) => ({
        url: `/survey/results`,
        method: METHODS.get,
        params: { surveyId },
      }),
      providesTags: ['SurveyResponses'],
    }),
    closeSurvey: builder.mutation<{ survey: Survey }, string>({
      query: (surveyId) => ({
        url: `/survey/close`,
        method: METHODS.post,
        params: { surveyId },
      }),
      invalidatesTags: ['Surveys'],
    }),
    deleteSurvey: builder.mutation<{ success: boolean }, string>({
      query: (surveyId) => ({
        url: `/survey/remove`,
        method: METHODS.post,
        params: { surveyId },
      }),
      invalidatesTags: ['Surveys'],
    }),
  }),
});

export const {
  useGetSurveysQuery,
  useGetSurveyQuery,
  useCreateSurveyMutation,
  useSubmitResponseMutation,
  useGetSurveyResultsQuery,
  useCloseSurveyMutation,
  useDeleteSurveyMutation,
} = SurveyApi;

export { SurveyApi };
export type {
  Survey,
  SurveyQuestion,
  SurveyResponse,
  SurveyResult,
  SurveyListQuery,
  CreateSurveyInput,
  SubmitResponseInput,
};
